import { zodResolver } from "@hookform/resolvers/zod";
import { useAnalytics } from "@restore-hw/analytics";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { useAuth } from "@/components/Auth";
import AutoRenewSavingsModal from "@/components/AutoRenewSavingsModal";
import Button from "@/components/Button";
import { MarketingConsent } from "@/components/Common";
import ErrorText from "@/components/ErrorText";
import FormField from "@/components/FormField";
import FormFields from "@/components/FormFields";
import Header from "@/components/Header";
import Icon from "@/components/Icon";
import { Input, Textarea } from "@/components/Input";
import Label from "@/components/Label";
import Link from "@/components/Link";
import Loader from "@/components/Loader";
import { useNotification } from "@/components/Notification";
import { AddCardForm } from "@/components/Stripe";
import { APP_ID } from "@/constants/appIdentifier";
import { ExternalLinks } from "@/constants/links";
import { ErrorMessages } from "@/constants/messages";
import { PageRoute, PageTitle } from "@/constants/pages";
import { useEnrollToAutoRenewMutation } from "@/hooks/autoRenew";
import { useBookAppointmentMutation, useServicesQuery } from "@/hooks/booking";
import { useAutoRenewContent } from "@/hooks/cms";
import { usePageEventTracking } from "@/hooks/event-tracking";
import {
  useSetUserHasCardOnFile,
  useUpdateCommunicationSettingsMutation,
  useUpdateUserMutation,
} from "@/hooks/user";
import type { BookAppointmentResponse, Store } from "@/services/booking";
import { isSet } from "@/utils/helpers";

import { inprogressBookingSchema } from "./types";

const schema = z.object({
  notes: z.string(),
  marketingTexts: z.boolean(),
  phone: z.string().min(10, {
    message: "10 digit phone number is required",
  }),
});

type Schema = z.infer<typeof schema>;

type Props = {
  store: Store;
};

export default function BookingConfirmPage({ store }: Props) {
  const { analytics } = useAnalytics();
  const location = useLocation();
  const inprogressBooking = useMemo(() => {
    const result = inprogressBookingSchema.safeParse(location.state);

    if (result.success) {
      return result.data;
    }
  }, [location.state]);
  const notify = useNotification();
  const { user } = useAuth();
  const updateUserMutation = useUpdateUserMutation();
  const [bookingErrorText, setBookingErrorText] = useState<string | null>(null);
  const [showAutoRenewModal, setShowAutoRenewModal] = useState(false);

  const bookAppointmentMutation = useBookAppointmentMutation();
  const updateCommunicationSettingsMutation =
    useUpdateCommunicationSettingsMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: { marketingTexts: true, phone: user?.mobile_phone ?? "" },
  });
  const setUserHasCardOnFile = useSetUserHasCardOnFile();
  const servicesQuery = useServicesQuery(store.store_code);
  const navigate = useNavigate();
  const isPhoneFieldsRequired = !user?.mobile_phone;

  // Auto-Renew related hooks
  const autoRenewContentQuery = useAutoRenewContent();
  const _enrollToAutoRenewMutation = useEnrollToAutoRenewMutation();

  const service = useMemo(() => {
    if (servicesQuery.data && inprogressBooking) {
      return servicesQuery.data
        .flatMap((appointmentType) => appointmentType.services)
        .find((service) => service.id === Number(inprogressBooking.serviceId));
    }
  }, [servicesQuery.data, inprogressBooking]);

  // Auto-Renew modal is disabled because the backend integration is not ready yet
  // Check if we should show Auto-Renew modal
  const shouldShowAutoRenewModal = useMemo(
    () => {
      // Temporarily disabled - backend integration not ready
      return false;

      // Original logic (commented out):
      // if (!service || !inprogressBooking) return false;
      // const isNotEnrolled = !autoRenewStatusQuery.data?.isEnrolled;
      // return isIVNutrientsService && isNotEnrolled;
    },
    [
      // service,
      // inprogressBooking,
      // isIVNutrientsService,
      // autoRenewStatusQuery.data?.isEnrolled,
    ],
  );

  usePageEventTracking({
    service_name: service?.name,
    service_date: inprogressBooking?.time.appointment_datetime,
  });

  useDocumentTitle(PageTitle.APPOINTMENT_CONFIRM);

  if (!inprogressBooking) {
    return <Navigate to={PageRoute.APPOINTMENT_BOOK} />;
  }

  const isPaymentMethodRequired =
    !user?.has_card_on_file && service?.cancel_requires_payment;

  const handleCompleteBooking = async (formData: Schema) => {
    const { marketingTexts, phone, notes } = formData;

    if (isPhoneFieldsRequired) {
      try {
        await Promise.all([
          updateCommunicationSettingsMutation.mutateAsync({
            payload: {
              text: {
                promo: marketingTexts,
              },
            },
          }),
          updateUserMutation.mutateAsync({
            mobile_phone: phone,
          }),
        ]);
      } catch (_error) {
        return;
      }
    }
    if (analytics) {
      analytics.bookingAppointmentStarted({
        executed_by: APP_ID,
        step: 1,
      });
    }

    setBookingErrorText(null);

    bookAppointmentMutation.mutate(
      {
        notes,
        storeCode: store.store_code,
        serviceId: inprogressBooking.serviceId,
        time: inprogressBooking.time.appointment_datetime,
      },
      {
        onSuccess: (response) => {
          switch (response.type) {
            case "success": {
              navigate(
                {
                  pathname: "/book/success",
                },
                {
                  state: response.appointment,
                } satisfies { state: BookAppointmentResponse },
              );
              break;
            }
            case "noSlotsAvailable": {
              setBookingErrorText(
                "Sorry, that day/time is no longer available",
              );
              break;
            }
            case "error": {
              setBookingErrorText(ErrorMessages.GENERIC);
              break;
            }
          }
        },
      },
    );
  };

  const onSubmit = handleSubmit(async (formData) => {
    if (shouldShowAutoRenewModal) {
      setShowAutoRenewModal(true);
    } else {
      await handleCompleteBooking(formData);
    }
  });

  const handleAutoRenewModalClose = () => {
    setShowAutoRenewModal(false);
    // Complete the booking when user dismisses the modal
    void handleSubmit(handleCompleteBooking)();
  };

  const handleAutoRenewEnroll = () => {
    setShowAutoRenewModal(false);
    // TODO: Implement the "Enroll to Auto-Renew" modal in a separate task.
    // For now, just complete the booking.
    void handleSubmit(handleCompleteBooking)();

    // enrollToAutoRenewMutation.mutate(store.store_code, {
    //   onSuccess: () => {
    //     notify("You've been enrolled in Auto-Renew!", "success");
    //   },
    //   onError: (error) => {
    //     notify(
    //       `Could not enroll in Auto-Renew: ${error.message}. Proceeding with booking.`,
    //       "error"
    //     );
    //   },
    //   onSettled: () => {
    //     void handleSubmit(handleCompleteBooking)();
    //   },
    // });
  };

  function handleAddCardSuccess() {
    notify("Card has been added");
    setUserHasCardOnFile();

    // After adding card, check if we should show Auto-Renew modal
    if (shouldShowAutoRenewModal) {
      setShowAutoRenewModal(true);
    } else {
      // If no modal needed, proceed with booking
      void handleSubmit(handleCompleteBooking)();
    }
  }

  if (!service) {
    return <Loader />;
  }

  const formattedTime = format(
    new Date(inprogressBooking.time.appointment_datetime),
    "p",
  );

  const formattedTimeLine = isSet(inprogressBooking.duration)
    ? `${formattedTime} (${inprogressBooking.duration} minutes)`
    : formattedTime;

  return (
    <>
      <Header className="mb-6">Submit your Appointment Details</Header>

      <div className="mb-6 flex flex-col gap-4 rounded-md bg-amalfi-coast/10 p-4 font-semibold text-amalfi-coast">
        <Link href={ExternalLinks.LOCATIONS} className="flex gap-2">
          <div className="flex w-[25px] items-center justify-center">
            <Icon name="map" />
          </div>
          {store.description}
        </Link>
        <Link
          onClick={() =>
            navigate(PageRoute.APPOINTMENT_BOOK, { state: inprogressBooking })
          }
          className="flex gap-2"
        >
          <div className="flex w-[25px] items-center justify-center">
            <Icon name="restore" />
          </div>
          {service.name}
        </Link>
        <Link
          onClick={() =>
            navigate(PageRoute.APPOINTMENT_BOOK, { state: inprogressBooking })
          }
          className="flex gap-2"
        >
          <div className="flex w-[25px] items-center justify-center">
            <Icon name="calendar" />
          </div>
          {format(new Date(inprogressBooking.time.appointment_datetime), "P")}
          <div className="ml-5 flex w-[25px] items-center justify-center">
            <Icon name="clock" />
          </div>
          {formattedTimeLine}
        </Link>
      </div>

      {isPaymentMethodRequired ? (
        <>
          <Header className="mb-6 text-xl" as="h2">
            Late Cancellation
          </Header>

          <p className="mb-6">
            We require a valid credit or debit card on file for No Show and Late
            Cancellation fees within 24 hours of your appointment start time.
          </p>

          <div className="bg-linx-white p-4">
            <AddCardForm onSuccess={handleAddCardSuccess} />
          </div>
        </>
      ) : (
        <form onSubmit={onSubmit}>
          <FormFields className="mb-2">
            <FormField>
              <Label htmlFor="notes">Note for staff</Label>
              <Textarea rows={3} id="notes" {...register("notes")} />
            </FormField>
            {isPhoneFieldsRequired && (
              <FormField>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" type="tel" {...register("phone")} />

                {errors.phone && <ErrorText>{errors.phone.message}</ErrorText>}
              </FormField>
            )}

            {isPhoneFieldsRequired && (
              <FormField className="mb-2">
                <div className="flex items-start gap-2">
                  <MarketingConsent
                    className={"text-center"}
                    hidePrivacyPolicy
                  />
                </div>
              </FormField>
            )}

            {bookingErrorText && <ErrorText error={bookingErrorText} />}

            {updateUserMutation.isError && (
              <ErrorText error={updateUserMutation.error} />
            )}

            {bookAppointmentMutation.isPending ||
            updateUserMutation.isPending ||
            updateCommunicationSettingsMutation.isPending ? (
              <Loader />
            ) : (
              <Button type="submit">Complete booking</Button>
            )}
          </FormFields>

          <p className="text-center text-xs leading-5">
            {service.cancel_requires_payment && (
              <>
                This service is subject to No Show and Late Cancellation fees.
                <br />
              </>
            )}
            <>
              By confirming your appointment you agree to our <br />
              <Link href={ExternalLinks.TERMS} target="_blank">
                cancellation policy
              </Link>{" "}
              and{" "}
              <Link href={ExternalLinks.PRIVACY_POLICY} target="_blank">
                privacy policy
              </Link>
              .
            </>
          </p>
        </form>
      )}

      <AutoRenewSavingsModal
        isOpen={showAutoRenewModal}
        onClose={handleAutoRenewModalClose}
        onEnroll={handleAutoRenewEnroll}
        body={autoRenewContentQuery.data?.body}
        ctaText={autoRenewContentQuery.data?.ctaText}
      />
    </>
  );
}
